import {
  isAllowedMediaMime,
  normalizeMime,
  type MediaAsset,
  type PresignedUpload,
} from '@aysar/contracts';

import { type RunMediaUploadInput, type UploadFailureReason } from './run-media-upload.types';

/** An upload that stopped, carrying the reason the UI turns into a line of copy. */
export class UploadError extends Error {
  constructor(
    readonly reason: UploadFailureReason,
    override readonly cause?: unknown,
  ) {
    super(`Upload failed: ${reason}`);
    this.name = 'UploadError';
  }
}

/**
 * Header names the browser reserves for itself.
 *
 * `content-length` is **signed** by the api (`SIGNED_PUT_HEADERS` in
 * `s3-object-storage.adapter.ts`) — that signature is what makes the size limit
 * a server-enforced constraint rather than a client-side suggestion — but it is
 * also a forbidden header name for `XMLHttpRequest`, which **throws** on
 * `setRequestHeader`. It is skipped rather than sent, and the signature still
 * matches: the browser sets `Content-Length` itself, from the body, to exactly
 * the byte count that was signed.
 */
const BROWSER_OWNED_HEADERS = new Set(['content-length']);

/**
 * The PUT itself, over `XMLHttpRequest` rather than `fetch`.
 *
 * `fetch` reports **no** progress for a request body — there is no upload-side
 * `ReadableStream` in any shipping browser — so a percentage over it could only
 * ever be invented. XHR's `upload.onprogress` is the only transport that can
 * say how many bytes have actually left, and an upload with a real bar is the
 * difference between "it is working" and "is it stuck?" on a 4 MB logo over a
 * bad connection.
 *
 * Everything else is preserved exactly: the mint's own method and headers, the
 * abort signal, and the status classification.
 */
function putBytes(
  presigned: PresignedUpload,
  file: File,
  onProgress: RunMediaUploadInput['onProgress'],
  signal: AbortSignal | undefined,
): Promise<number> {
  return new Promise<number>((resolve, reject) => {
    // Re-checked HERE, not only before the presign. An abort that lands during
    // the presign round-trip would otherwise be missed entirely: a listener
    // added to an already-aborted signal is never invoked, so the PUT would run
    // to completion for an upload the operator had already cancelled.
    if (signal?.aborted === true) {
      reject(new UploadError('canceled'));
      return;
    }

    const request = new XMLHttpRequest();

    request.open(presigned.method, presigned.url, true);

    for (const [name, value] of Object.entries(presigned.headers)) {
      if (BROWSER_OWNED_HEADERS.has(name.toLowerCase())) {
        continue;
      }
      request.setRequestHeader(name, value);
    }

    if (onProgress !== undefined) {
      request.upload.onprogress = (event) => {
        // `lengthComputable` false means the transport genuinely does not know;
        // reporting 0 of 0 would render as a bar pinned at either end.
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        }
      };
    }

    const abort = () => {
      request.abort();
    };

    const settle = (outcome: () => void) => {
      signal?.removeEventListener('abort', abort);
      outcome();
    };

    request.onload = () => {
      settle(() => {
        resolve(request.status);
      });
    };
    // Both are "the bytes never got there", which is a different thing from
    // storage refusing them, and the operator's next move differs too.
    request.onerror = () => {
      settle(() => {
        reject(new UploadError('network'));
      });
    };
    request.ontimeout = () => {
      settle(() => {
        reject(new UploadError('network'));
      });
    };
    request.onabort = () => {
      settle(() => {
        reject(new UploadError('canceled'));
      });
    };

    signal?.addEventListener('abort', abort, { once: true });

    request.send(file);
  });
}

/**
 * The three-step upload the platform mandates (N12): **presign → PUT → finalize**.
 *
 * Four properties are load-bearing, and each is asserted.
 *
 * **The PUT uses the `method` and `headers` the server minted.** Not a
 * hardcoded `PUT` with a hand-built `Content-Type`: the presigned signature
 * covers those headers, so an added, dropped or re-cased one is an upload S3
 * rejects — and the failure surfaces as a mystery 403 with the bytes already
 * sent. The one exception is `content-length`, which the browser owns; see
 * `BROWSER_OWNED_HEADERS`.
 *
 * **A bad MIME is refused before any network call.** The kind registry already
 * knows which types the server can decode, so uploading first and learning at
 * finalize wastes the operator's bytes and leaves a reserved row for the purge
 * worker to sweep.
 *
 * **A failed PUT short-circuits — finalize is never called.** Finalize means
 * "the bytes are there, go read them"; calling it after a failed transfer asks
 * the server to decode an object that does not exist, and turns a clean upload
 * failure into a confusing server-side one.
 *
 * **A cancel is its own outcome.** The operator who pressed Cancel does not need
 * to be told the upload was interrupted.
 *
 * The client never sees a storage key or builds a URL: it only ever uses the one
 * it was handed.
 */
export async function runMediaUpload<TFinalized = MediaAsset>({
  file,
  kind,
  maxBytes,
  presign,
  finalize,
  sortOrder,
  signal,
  onProgress,
}: RunMediaUploadInput<TFinalized>): Promise<TFinalized> {
  const mime = normalizeMime(file.type);

  if (mime === null || !isAllowedMediaMime(kind, mime)) {
    throw new UploadError('wrongType');
  }

  if (maxBytes !== undefined && file.size > maxBytes) {
    throw new UploadError('tooLarge');
  }

  // Checked before the presign rather than only inside the transport: a signal
  // that was already aborted would otherwise reserve a row and mint a signature
  // for an upload nobody is waiting for, leaving work for the purge worker.
  if (signal?.aborted === true) {
    throw new UploadError('canceled');
  }

  const presigned = await presign({ kind, mime, sizeBytes: file.size });

  const status = await putBytes(presigned, file, onProgress, signal);

  if (status < 200 || status >= 300) {
    // 403 from object storage is nearly always an expired or mismatched
    // signature; telling the operator to "try again" is then the correct advice,
    // where "the upload failed" invites them to go looking at the file.
    throw new UploadError(status === 403 ? 'expired' : 'failed');
  }

  return finalize(presigned.mediaId, sortOrder === undefined ? {} : { sortOrder });
}
